import { getAccessToken } from "config/token";
import { IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse } from "axios";
import { Credentials } from "../components/Users/components/AccessData/types";
import { NEXUS_API_BASE_URL } from "const";


const KEY_DB_NAME = "nexus-keystore";
const KEY_STORE_NAME = "keys";
const KEY_ID = "awsSession";

let cryptoKeyPromise: Promise<CryptoKey> | null = null;

function generateCryptoKey(): Promise<CryptoKey> {
  // Non-extractable: the browser can use the key but JS can never read its bytes.
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function openKeyDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(KEY_DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(KEY_STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadOrCreatePersistedKey(): Promise<CryptoKey> {
  const db = await openKeyDb();
  try {
    const existing = await idbRequest<CryptoKey | undefined>(
      db.transaction(KEY_STORE_NAME, "readonly").objectStore(KEY_STORE_NAME).get(KEY_ID),
    );
    if (existing) {
      return existing;
    }
    const key = await generateCryptoKey();
    await idbRequest(
      db.transaction(KEY_STORE_NAME, "readwrite").objectStore(KEY_STORE_NAME).put(key, KEY_ID),
    );
    return key;
  } finally {
    db.close();
  }
}

/**
 * Get this browser's AES-GCM key, persisted in IndexedDB so no secret ships in the bundle.
 * Falls back to an in-memory key (session re-fetched after reload) if IndexedDB is unavailable.
 */
function getCryptoKey(): Promise<CryptoKey> {
  if (!cryptoKeyPromise) {
    cryptoKeyPromise =
      typeof indexedDB === "undefined"
        ? generateCryptoKey()
        : loadOrCreatePersistedKey().catch(err => {
          console.warn("IndexedDB key store unavailable, using in-memory key:", err);
          return generateCryptoKey();
        });
  }
  return cryptoKeyPromise;
}

/**
 * Encrypt a JSON string and return a base64 blob containing iv+ciphertext.
 */
export async function encryptSession(plaintext: string): Promise<string> {
  const key = await getCryptoKey();
  const iv  = crypto.getRandomValues(new Uint8Array(12));
  const ct  = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  const combined = new Uint8Array(iv.byteLength + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt the base64 blob back into the original JSON string.
 */
async function decryptSession(blob: string): Promise<string> {
  const data = Uint8Array.from(atob(blob), c => c.charCodeAt(0));
  const iv   = data.slice(0, 12);
  const ct   = data.slice(12);
  const key  = await getCryptoKey();
  const pt   = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ct,
  );
  return new TextDecoder().decode(pt);
}

/**
 * Encrypts and stores the AWS session in localStorage under "awsSession".
 */
export async function storeAwsSession(raw: Credentials): Promise<void> {
  const blob = await encryptSession(JSON.stringify(raw));
  localStorage.setItem("awsSession", blob);
}

/**
 * Loads, decrypts, parses, and validates the AWS session.
 * Throws if missing, invalid, or expired (with 60s buffer).
 */
export async function loadAwsSession(): Promise<Credentials> {
  const blob = localStorage.getItem("awsSession");

  if (!blob) {
    throw new Error("No AWS session stored");
  }

  let json: string;
  try {
    json = await decryptSession(blob);
   
  } catch (err) {
    throw new Error("Failed to decrypt AWS session");
  }

  if (!json) {
    throw new Error("Decrypted session is empty");
  }

  let session: Credentials;
  try {
    session = JSON.parse(json);
  } catch (err) {

    throw new Error("Invalid AWS session JSON");
  }

  // Validate expiration (60s buffer)
  if (Date.now() / 1000 > session.ExpirationTimestamp - 60) {
    throw new Error("AWS credentials expired");
  }

  return session;
}

export const generateKeysApi = async (
  msalInstance: IMsalContext,
): Promise<any> => {
  const token = await getAccessToken(msalInstance);

  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
  const requestURL: string = NEXUS_API_BASE_URL + "/nexus/get-keys";

  const config = {
    headers: { Authorization: bearerToken },
  };

  let keysResponse:unknown = null;

  try {
    const res: AxiosResponse = await axios.get(requestURL, config);
    keysResponse = res.data;
    return keysResponse;
  } catch (error) {
    console.error("Error fetching Keys:", error);
    keysResponse = null;
    return keysResponse;
  }
};
